import { Component } from '@theme/component';
import { VariantSelectedEvent, VariantUpdateEvent } from '@theme/events';
import { morph, MORPH_OPTIONS } from '@theme/morph';
import { yieldToMainThread, getViewParameterValue, ResizeNotifier } from '@theme/utilities';

/**
 * @typedef {object} VariantPickerRefs
 * @property {HTMLFieldSetElement[]} fieldsets – The fieldset elements.
 */

/**
 * A custom element that manages a variant picker.
 *
 * @template {import('@theme/component').Refs} [TRefs=VariantPickerRefs]
 * @extends Component<TRefs>
 */
export default class VariantPicker extends Component {
  /** @type {string | undefined} */
  #pendingRequestUrl;

  /** @type {AbortController | undefined} */
  #abortController;

  /** @type {number[][]} */
  #checkedIndices = [];

  /** @type {HTMLInputElement[][]} */
  #radios = [];

  #resizeObserver = new ResizeNotifier(() => this.updateVariantPickerCss());

  connectedCallback() {
    super.connectedCallback();
    const fieldsets = /** @type {HTMLFieldSetElement[]} */ (this.refs.fieldsets || []);

    fieldsets.forEach((fieldset) => {
      const radios = Array.from(fieldset?.querySelectorAll('input') ?? []);
      this.#radios.push(radios);

      const initialCheckedIndex = radios.findIndex((radio) => radio.dataset.currentChecked === 'true');
      if (initialCheckedIndex !== -1) {
        this.#checkedIndices.push([initialCheckedIndex]);
      }
    });

    this.addEventListener('change', this.variantChanged.bind(this));
    this.#resizeObserver.observe(this);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#resizeObserver.disconnect();
  }

  /**
   * Handles the variant change event.
   * @param {Event} event - The variant change event.
   */
  variantChanged(event) {
    if (!(event.target instanceof HTMLElement)) return;

    const selectedOption =
      event.target instanceof HTMLSelectElement ? event.target.options[event.target.selectedIndex] : event.target;

    if (!selectedOption) return;

    if (this.dataset.schoolButtonStandard === 'true' && event.target instanceof HTMLInputElement) {
      if (event.target.disabled) return;
      const optionNames = JSON.parse(this.dataset.schoolOptionNames || '[]');
      const sizeIndex = optionNames.findIndex(name => name.toLowerCase() === 'size');
      const colorIndex = optionNames.findIndex(name => /color|colour|style/i.test(name));
      if (sizeIndex >= 0 && colorIndex >= 0 && event.target.name.startsWith(`${optionNames[sizeIndex]}-`)) {
        const variants = JSON.parse(this.dataset.schoolVariants || this.dataset.performanceVariants || this.dataset.melissaVariants || '[]');
        const size = event.target.value;
        const colorField = this.querySelector('[data-school-option-role="style"]');
        const current = colorField?.querySelector('input:checked');
        const toddler = /T$/i.test(size) || /toddler/i.test(size);
        const candidates = variants.filter(v => v.options[sizeIndex] === size && v.available && !(toddler && /^Comfort Colors/i.test(v.options[colorIndex])));
        if (current && candidates.length && !candidates.some(v => v.options[colorIndex] === current.value)) {
          const maker = value => {
            const brand = value.includes(' – ') ? value.split(' – ')[0] : '';
            return this.dataset.productId === '15446264774937' ? brand.replace(/ youth$/i, '') : brand;
          };
          const hue = value => {
            const raw = value.split(' – ').pop().toLowerCase();
            if (['red','crimson','scarlet','sport red'].includes(raw)) return 'red';
            if (['gray','grey','sport gray','sport grey','heather','sport silver','silver'].includes(raw)) return 'gray';
            return raw;
          };
          const sameMaker = candidates.filter(v => maker(v.options[colorIndex]) === maker(current.value));
          const match = sameMaker.find(v => hue(v.options[colorIndex]) === hue(current.value)) || sameMaker.find(v => hue(v.options[colorIndex]) === 'white') || sameMaker[0] || candidates.find(v => hue(v.options[colorIndex]) === hue(current.value)) || candidates.find(v => hue(v.options[colorIndex]) === 'white') || candidates[0];
          const fallback = Array.from(colorField.querySelectorAll('input')).find(input => input.value === match.options[colorIndex]);
          if (fallback instanceof HTMLInputElement) this.updateSelectedOption(fallback);
        }
      }
    }

    if (this.dataset.productId === '8726497624345' && event.target instanceof HTMLInputElement) {
      if (event.target.disabled) return;
      if (event.target.name.startsWith('Size-')) {
        const variants = JSON.parse(this.dataset.melissaVariants || '[]');
        const size = event.target.value;
        const colors = this.querySelector('[data-option-name="Color"]');
        const current = colors?.querySelector('input:checked');
        const candidates = variants.filter(v => v.option1 === size && v.available);
        if (current && !candidates.some(v => v.option2 === current.value)) {
          const raw = String(current.value).split(' – ').pop();
          const hue = value => ['Crimson','Red'].includes(value) ? 'Red' : ['Grey','Sport Grey','Sport Gray','Heather'].includes(value) ? 'Gray' : value;
          const match = candidates.find(v => hue(String(v.option2).split(' – ').pop()) === hue(raw)) || candidates.find(v => v.option2.endsWith(' – White')) || candidates[0];
          const fallback = Array.from(colors?.querySelectorAll('input') || []).find(input => input.value === match?.option2);
          if (fallback instanceof HTMLInputElement) this.updateSelectedOption(fallback);
        }
      }
    }

    if (this.dataset.tt41Sop === 'true' && event.target instanceof HTMLInputElement) {
      if (event.target.disabled) return;
      const variants = JSON.parse(this.dataset.performanceVariants || '[]');
      const sizeField = this.querySelector('[data-option-name="Size"]');
      const colorField = this.querySelector('[data-option-name="Color"]');
      const size = sizeField?.querySelector('input:checked')?.value;
      const color = colorField?.querySelector('input:checked')?.value;
      if (event.target.closest('[data-option-name="Color"]') && !variants.some(v => v.option1 === size && v.option2 === color && v.available)) {
        const match = variants.find(v => v.option2 === color && v.available && (String(v.option1).startsWith('Youth ') === String(size).startsWith('Youth '))) || variants.find(v => v.option2 === color && v.available);
        const fallback = Array.from(sizeField?.querySelectorAll('input') || []).find(input => input.value === match?.option1);
        if (fallback instanceof HTMLInputElement) this.updateSelectedOption(fallback);
      }
    }


    if (this.dataset.farmersvilleFix === 'true' && event.target instanceof HTMLInputElement) {
      const size = this.querySelector('[data-option-name="Size"] input:checked')?.value;
      const colorField = this.querySelector('[data-option-name="Color"]');
      const unsupportedColor = (color) =>
        (!['8726424584473', '8798095671577', '9658642825497'].includes(this.dataset.productId) && /^(2T|3T|4T)$/.test(size || '') && ['Dark Purple', 'Gold'].includes(color)) ||
        (['4XL', '5XL'].includes(size || '') && color === 'Gold');
      if (event.target.closest('[data-option-name="Color"]') &&
          (selectedOption.dataset.optionAvailable === 'false' || unsupportedColor(event.target.value))) {
        event.target.checked = false;
        const previous = colorField?.querySelector('input[data-current-checked="true"]');
        if (previous instanceof HTMLInputElement) previous.checked = true;
        event.stopImmediatePropagation();
        return;
      }
      if (event.target.closest('[data-option-name="Size"]') && colorField) {
        const currentColor = colorField.querySelector('input:checked');
        if (currentColor instanceof HTMLInputElement && unsupportedColor(currentColor.value)) {
          const fallback = colorField.querySelector('input[value="Black"]');
          if (fallback instanceof HTMLInputElement) this.updateSelectedOption(fallback);
        }
        colorField.querySelectorAll('input').forEach((input) => {
          if (input instanceof HTMLInputElement) {
            input.disabled = unsupportedColor(input.value);
            if (input.disabled) input.setAttribute('aria-disabled', 'true');
            else input.removeAttribute('aria-disabled');
          }
        });
      }
    }

    if (['8773230133529', '8798107369753', '10021796708633', '9658642366745', '15436076974361', '10021810995481', '10025159524633', '10021792973081', '9950516773145', '9939200704793', '9785200673049', '9657397903641', '9667661594905', '9088909508889', '9088906002713', '8840176828697', '9088906854681', '15413690859801'].includes(this.dataset.productId) && event.target instanceof HTMLInputElement &&
        event.target.name.startsWith('Size-')) {
      const colorField = this.querySelector('input[name^="Color-"]')?.closest('fieldset');
      const currentColor = colorField?.querySelector('input:checked');
      const toddler = /^[234]T$/.test(event.target.value);
      const youth = event.target.value.startsWith('Youth ');
      if (currentColor instanceof HTMLInputElement &&
          ((toddler && currentColor.value.startsWith('Comfort Colors')) ||
           (youth && ['Comfort Colors – Mustard', 'Comfort Colors – Moss'].includes(currentColor.value)))) {
        const fallbackValue = youth ? 'Comfort Colors – White' : 'White';
        const fallback = Array.from(colorField.querySelectorAll('input')).find(input => input.value === fallbackValue);
        if (fallback instanceof HTMLInputElement) this.updateSelectedOption(fallback);
      }
    }

    if (['8840147763481', '9707920982297', '10271814353177', '8798096785689'].includes(this.dataset.productId) && event.target instanceof HTMLInputElement &&
        event.target.name.startsWith('Size-')) {
      const colorField = this.querySelector('input[name^="Color-"]')?.closest('fieldset');
      const currentColor = colorField?.querySelector('input:checked');
      const size = event.target.value;
      const toddler = ['2T', '4T', '5/6T'].includes(size);
      const youth = size.startsWith('Youth ');
      const extended = ['4XL', '5XL'].includes(size);
      const unsupported = color => toddler ? !color.startsWith('Rabbit Skins') :
        youth ? !['Gildan – White', 'Gildan – Black', 'Gildan – Sport Gray'].includes(color) :
        color.startsWith('Rabbit Skins') || (extended && !['Gildan – White', 'Gildan – Black', 'Gildan – Sport Gray'].includes(color));
      if (currentColor instanceof HTMLInputElement && unsupported(currentColor.value)) {
        const value = toddler ? 'Rabbit Skins Toddler – White' : extended ? 'Gildan – Black' : 'Gildan – White';
        const fallback = Array.from(colorField.querySelectorAll('input')).find(input => input.value === value);
        if (fallback instanceof HTMLInputElement) this.updateSelectedOption(fallback);
      }
    }

    if (['9950516773145', '9939200704793'].includes(this.dataset.productId) && event.target instanceof HTMLInputElement &&
        event.target.name.startsWith('Size-') && event.target.value === '4XL') {
      const colorField = this.querySelector('input[name^="Color-"]')?.closest('fieldset');
      const fallback = Array.from(colorField?.querySelectorAll('input') || []).find(input => input.value === 'Sport Gray');
      if (fallback instanceof HTMLInputElement) this.updateSelectedOption(fallback);
    }

    if (this.dataset.productId === '9785200673049' && event.target instanceof HTMLInputElement &&
        event.target.name.startsWith('Size-') && event.target.value === '4XL') {
      const colorField = this.querySelector('input[name^="Color-"]')?.closest('fieldset');
      const fallback = Array.from(colorField?.querySelectorAll('input') || []).find(input => input.value === 'Dark Purple');
      if (fallback instanceof HTMLInputElement) this.updateSelectedOption(fallback);
    }

    if (this.dataset.productId === '9657397903641' && event.target instanceof HTMLInputElement && event.target.name.startsWith('Size-') && event.target.value === '4XL') {
      const colorField = this.querySelector('input[name^="Color-"]')?.closest('fieldset');
      const fallback = Array.from(colorField?.querySelectorAll('input') || []).find(input => input.value === 'White');
      if (fallback instanceof HTMLInputElement) this.updateSelectedOption(fallback);
    }

    if (this.dataset.productId === '9667661594905' && event.target instanceof HTMLInputElement && event.target.name.startsWith('Size-') && event.target.value === '4XL') {
      const colorField = this.querySelector('input[name^="Color-"]')?.closest('fieldset');
      const fallback = Array.from(colorField?.querySelectorAll('input') || []).find(input => input.value === 'Black');
      if (fallback instanceof HTMLInputElement) this.updateSelectedOption(fallback);
    }

    if (this.dataset.productId === '9088909508889' && event.target instanceof HTMLInputElement && event.target.name.startsWith('Size-') && event.target.value === '4XL') {
      const colorField = this.querySelector('input[name^="Color-"]')?.closest('fieldset');
      const currentColor = colorField?.querySelector('input:checked');
      if (currentColor instanceof HTMLInputElement && !['Black', 'Sport Gray'].includes(currentColor.value)) {
        const fallback = Array.from(colorField?.querySelectorAll('input') || []).find(input => input.value === 'Black');
        if (fallback instanceof HTMLInputElement) this.updateSelectedOption(fallback);
      }
    }

    if (this.dataset.productId === '15413690859801' && event.target instanceof HTMLInputElement && event.target.name.startsWith('Size-') && event.target.value === '4XL') {
      const colorField = this.querySelector('input[name^="Color-"]')?.closest('fieldset');
      const currentColor = colorField?.querySelector('input:checked');
      if (currentColor instanceof HTMLInputElement && !['White', 'Black', 'Sport Gray', 'Dark Purple'].includes(currentColor.value)) {
        const fallback = Array.from(colorField?.querySelectorAll('input') || []).find(input => input.value === 'Black');
        if (fallback instanceof HTMLInputElement) this.updateSelectedOption(fallback);
      }
    }

    if (this.dataset.productId === '9088906854681' && event.target instanceof HTMLInputElement && event.target.name.startsWith('Size-') && event.target.value === '4XL') {
      const colorField = this.querySelector('input[name^="Color-"]')?.closest('fieldset');
      const currentColor = colorField?.querySelector('input:checked');
      if (currentColor instanceof HTMLInputElement && !['Black', 'Sport Gray'].includes(currentColor.value)) {
        const fallback = Array.from(colorField?.querySelectorAll('input') || []).find(input => input.value === 'Black');
        if (fallback instanceof HTMLInputElement) this.updateSelectedOption(fallback);
      }
    }

    if (this.dataset.productId === '9088906002713' && event.target instanceof HTMLInputElement && event.target.name.startsWith('Size-') && ['4XL', '5XL'].includes(event.target.value)) {
      const colorField = this.querySelector('input[name^="Color-"]')?.closest('fieldset');
      const currentColor = colorField?.querySelector('input:checked');
      if (currentColor instanceof HTMLInputElement && !['White', 'Black', 'Sport Gray', 'Dark Purple'].includes(currentColor.value)) {
        const fallback = Array.from(colorField?.querySelectorAll('input') || []).find(input => input.value === 'Black');
        if (fallback instanceof HTMLInputElement) this.updateSelectedOption(fallback);
      }
    }

    if (this.dataset.productId === '8840176828697' && event.target instanceof HTMLInputElement && event.target.name.startsWith('Size-') && event.target.value === '4XL') {
      const colorField = this.querySelector('input[name^="Color-"]')?.closest('fieldset');
      const fallback = Array.from(colorField?.querySelectorAll('input') || []).find(input => input.value === 'Black');
      if (fallback instanceof HTMLInputElement) this.updateSelectedOption(fallback);
    }

    if (this.dataset.productId === '9088901218585' && event.target instanceof HTMLInputElement) {
      const colorField = this.querySelector('[data-option-name="Color"]');
      if (event.target.disabled) return;
      if (event.target.name.startsWith('Size-') && colorField) {
        const toddler = ['2T', '3T', '4T', '5/6T'].includes(event.target.value);
        const currentColor = colorField.querySelector('input:checked');
        const toddlerColors = ['White Solid / Black', 'Vintage Heather / Vintage Purple'];
        if (currentColor instanceof HTMLInputElement && toddler !== toddlerColors.includes(currentColor.value)) {
          const fallbackValue = toddler ? 'White Solid / Black' : 'White / Black';
          const fallback = Array.from(colorField.querySelectorAll('input')).find(input => input.value === fallbackValue);
          if (fallback instanceof HTMLInputElement) this.updateSelectedOption(fallback);
        }
      }
    }

    this.updateSelectedOption(event.target);
    this.dispatchEvent(new VariantSelectedEvent({
      id: selectedOption.dataset.optionValueId ?? '',
    }));

    const isOnProductPage =
      this.dataset.templateProductMatch === 'true' &&
      !event.target.closest('product-card') &&
      !event.target.closest('quick-add-dialog');

    // Morph the entire main content for combined listings child products, because changing the product
    // might also change other sections depending on recommendations, metafields, etc.
    const currentUrl = this.dataset.productUrl?.split('?')[0];
    const newUrl = selectedOption.dataset.connectedProductUrl;
    const loadsNewProduct = isOnProductPage && !!newUrl && newUrl !== currentUrl;
    const isOnFeaturedProductSection = Boolean(this.closest('featured-product-information'));

    const morphElementSelector = loadsNewProduct
      ? 'main'
      : isOnFeaturedProductSection
      ? 'featured-product-information'
      : undefined;

    this.fetchUpdatedSection(this.buildRequestUrl(selectedOption), morphElementSelector);

    const url = new URL(window.location.href);

    const variantId = selectedOption.dataset.variantId || null;

    if (isOnProductPage) {
      if (variantId) {
        url.searchParams.set('variant', variantId);
      } else {
        url.searchParams.delete('variant');
      }
    }

    // Change the path if the option is connected to another product via combined listing.
    if (loadsNewProduct) {
      url.pathname = newUrl;
    }

    if (url.href !== window.location.href) {
      yieldToMainThread().then(() => {
        history.replaceState({}, '', url.toString());
      });
    }
  }

  /**
   * @typedef {object} FieldsetMeasurements
   * @property {HTMLFieldSetElement} fieldset
   * @property {number | undefined} currentIndex
   * @property {number | undefined} previousIndex
   * @property {number | undefined} currentWidth
   * @property {number | undefined} previousWidth
   */

  /**
   * Gets measurements for a single fieldset (read phase).
   * @param {number} fieldsetIndex
   * @returns {FieldsetMeasurements | null}
   */
  #getFieldsetMeasurements(fieldsetIndex) {
    const fieldsets = /** @type {HTMLFieldSetElement[]} */ (this.refs.fieldsets || []);
    const fieldset = fieldsets[fieldsetIndex];
    const checkedIndices = this.#checkedIndices[fieldsetIndex];
    const radios = this.#radios[fieldsetIndex];

    if (!radios || !checkedIndices || !fieldset) return null;

    const [currentIndex, previousIndex] = checkedIndices;

    return {
      fieldset,
      currentIndex,
      previousIndex,
      currentWidth: currentIndex !== undefined ? radios[currentIndex]?.parentElement?.offsetWidth : undefined,
      previousWidth: previousIndex !== undefined ? radios[previousIndex]?.parentElement?.offsetWidth : undefined,
    };
  }

  /**
   * Applies measurements to a fieldset (write phase).
   * @param {FieldsetMeasurements} measurements
   */
  #applyFieldsetMeasurements({ fieldset, currentWidth, previousWidth, currentIndex, previousIndex }) {
    if (currentWidth) {
      fieldset.style.setProperty('--pill-width-current', `${currentWidth}px`);
    } else if (currentIndex !== undefined) {
      fieldset.style.removeProperty('--pill-width-current');
    }

    if (previousWidth) {
      fieldset.style.setProperty('--pill-width-previous', `${previousWidth}px`);
    } else if (previousIndex !== undefined) {
      fieldset.style.removeProperty('--pill-width-previous');
    }
  }

  /**
   * Updates the fieldset CSS.
   * @param {number} fieldsetIndex - The fieldset index.
   */
  updateFieldsetCss(fieldsetIndex) {
    if (Number.isNaN(fieldsetIndex)) return;

    const measurements = this.#getFieldsetMeasurements(fieldsetIndex);
    if (measurements) {
      this.#applyFieldsetMeasurements(measurements);
    }
  }

  /**
   * Updates the selected option.
   * @param {string | Element} target - The target element.
   */
  updateSelectedOption(target) {
    if (typeof target === 'string') {
      const targetElement = this.querySelector(`[data-option-value-id="${target}"]`);

      if (!targetElement) throw new Error('Target element not found');

      target = targetElement;
    }

    if (target instanceof HTMLInputElement) {
      const fieldsetIndex = Number.parseInt(target.dataset.fieldsetIndex || '');
      const inputIndex = Number.parseInt(target.dataset.inputIndex || '');

      if (!Number.isNaN(fieldsetIndex) && !Number.isNaN(inputIndex)) {
        const fieldsets = /** @type {HTMLFieldSetElement[]} */ (this.refs.fieldsets || []);
        const fieldset = fieldsets[fieldsetIndex];
        const checkedIndices = this.#checkedIndices[fieldsetIndex];
        const radios = this.#radios[fieldsetIndex];

        if (radios && checkedIndices && fieldset) {
          // Clear previous checked states
          const [currentIndex, previousIndex] = checkedIndices;

          if (currentIndex !== undefined && radios[currentIndex]) {
            radios[currentIndex].dataset.previousChecked = 'false';
          }
          if (previousIndex !== undefined && radios[previousIndex]) {
            radios[previousIndex].dataset.previousChecked = 'false';
          }

          // Update checked indices array - keep only the last 2 selections
          checkedIndices.unshift(inputIndex);
          checkedIndices.length = Math.min(checkedIndices.length, 2);

          // Update the new states
          const newCurrentIndex = checkedIndices[0]; // This is always inputIndex
          const newPreviousIndex = checkedIndices[1]; // This might be undefined

          // newCurrentIndex is guaranteed to exist since we just added it
          if (newCurrentIndex !== undefined && radios[newCurrentIndex]) {
            radios[newCurrentIndex].dataset.currentChecked = 'true';
          }

          if (newPreviousIndex !== undefined && radios[newPreviousIndex]) {
            radios[newPreviousIndex].dataset.previousChecked = 'true';
            radios[newPreviousIndex].dataset.currentChecked = 'false';
          }

          this.updateFieldsetCss(fieldsetIndex);
        }
      }
      target.checked = true;
    }

    if (target instanceof HTMLSelectElement) {
      const newValue = target.value;
      const newSelectedOption = Array.from(target.options).find((option) => option.value === newValue);

      if (!newSelectedOption) throw new Error('Option not found');

      for (const option of target.options) {
        option.removeAttribute('selected');
      }

      newSelectedOption.setAttribute('selected', 'selected');
    }
  }

  /**
   * Builds the request URL.
   * @param {HTMLElement} selectedOption - The selected option.
   * @param {string | null} [source] - The source.
   * @param {string[]} [sourceSelectedOptionsValues] - The source selected options values.
   * @returns {string} The request URL.
   */
  buildRequestUrl(selectedOption, source = null, sourceSelectedOptionsValues = []) {
    // this productUrl and pendingRequestUrl will be useful for the support of combined listing. It is used when a user changes variant quickly and those products are using separate URLs (combined listing).
    // We create a new URL and abort the previous fetch request if it's still pending.
    let productUrl = selectedOption.dataset.connectedProductUrl || this.#pendingRequestUrl || this.dataset.productUrl;
    this.#pendingRequestUrl = productUrl;
    const params = [];
    const viewParamValue = getViewParameterValue();

    // preserve view parameter, if it exists, for alternative product view testing
    if (viewParamValue) params.push(`view=${viewParamValue}`);

    if (this.selectedOptionsValues.length && !source) {
      params.push(`option_values=${this.selectedOptionsValues.join(',')}`);
    } else if (source === 'product-card') {
      if (this.selectedOptionsValues.length) {
        params.push(`option_values=${sourceSelectedOptionsValues.join(',')}`);
      } else {
        params.push(`option_values=${selectedOption.dataset.optionValueId}`);
      }
    }

    // If variant-picker is a child of some specific sections, we need to append section_id=xxxx to the URL
    const SECTION_ID_MAP = {
      'quick-add-component': 'section-rendering-product-card',
      'swatches-variant-picker-component': 'section-rendering-product-card',
      'featured-product-information': this.closest('featured-product-information')?.id,
    };

    const closestSectionId = /** @type {keyof typeof SECTION_ID_MAP} | undefined */ (
      Object.keys(SECTION_ID_MAP).find((sectionId) => this.closest(sectionId))
    );

    if (closestSectionId) {
      if (productUrl?.includes('?')) {
        productUrl = productUrl.split('?')[0];
      }
      return `${productUrl}?section_id=${SECTION_ID_MAP[closestSectionId]}&${params.join('&')}`;
    }

    if (this.dataset.schoolButtonStandard === 'true' && this.dataset.sectionId) {
      params.push(`section_id=${encodeURIComponent(this.dataset.sectionId)}`);
    }

    return `${productUrl}?${params.join('&')}`;
  }

  /**
   * Fetches the updated section.
   * @param {string} requestUrl - The request URL.
   * @param {string} [morphElementSelector] - The selector of the element to be morphed. By default, only the variant picker is morphed.
   */
  fetchUpdatedSection(requestUrl, morphElementSelector) {
    // We use this to abort the previous fetch request if it's still pending.
    this.#abortController?.abort();
    this.#abortController = new AbortController();

    fetch(requestUrl, { signal: this.#abortController.signal })
      .then((response) => response.text())
      .then((responseText) => {
        this.#pendingRequestUrl = undefined;
        const html = new DOMParser().parseFromString(responseText, 'text/html');
        // Defer is only useful for the initial rendering of the page. Remove it here.
        html.querySelector('overflow-list[defer]')?.removeAttribute('defer');

        const textContent = html.querySelector(`variant-picker script[type="application/json"]`)?.textContent;
        if (!textContent) return;

        let newProduct;

        if (morphElementSelector === 'main') {
          this.updateMain(html);
        } else if (morphElementSelector) {
          this.updateElement(html, morphElementSelector);
        } else {
          newProduct = this.updateVariantPicker(html);
        }

        if ((this.dataset.schoolButtonStandard === 'true' || ['9658642366745', '15436076974361', '10021810995481', '10025159524633', '10021792973081', '9950516773145', '9939200704793', '9785200673049', '10271814353177', '8798096785689', '8840147763481', '8726497624345'].includes(this.dataset.productId)) && !morphElementSelector) {
          const gallery = this.closest('.shopify-section')?.querySelector('media-gallery');
          const nextGallery = html.querySelector('media-gallery');
          if (gallery && nextGallery) gallery.replaceWith(nextGallery.cloneNode(true));
          const price = this.closest('.shopify-section')?.querySelector('product-price');
          const nextPrice = html.querySelector('product-price');
          if (price && nextPrice) price.replaceWith(nextPrice.cloneNode(true));
        }

        if (this.dataset.productId === '9088901218585' && !morphElementSelector) {
          const price = this.closest('.shopify-section')?.querySelector('product-price');
          const nextPrice = html.querySelector('product-price');
          if (price && nextPrice) price.replaceWith(nextPrice.cloneNode(true));
        }

        if (this.dataset.schoolButtonStandard === 'true' && this.dataset.templateProductMatch === 'true' && !this.closest('product-card, quick-add-dialog')) {
          const resolvedVariant = JSON.parse(textContent);
          if (resolvedVariant?.id) {
            const resolvedUrl = new URL(window.location.href);
            resolvedUrl.searchParams.set('variant', String(resolvedVariant.id));
            history.replaceState({}, '', resolvedUrl.toString());
          }
        }

        // Dispatch for all paths so product-form-component can reset #variantChangeInProgress
        if (this.selectedOptionId) {
          this.dispatchEvent(
            new VariantUpdateEvent(JSON.parse(textContent), this.selectedOptionId, {
              html,
              productId: this.dataset.productId ?? '',
              newProduct,
            })
          );
        }
      })
      .catch((error) => {
        if (error.name === 'AbortError') {
          console.warn('Fetch aborted by user');
        } else {
          console.error(error);
        }
      });
  }

  /**
   * @typedef {Object} NewProduct
   * @property {string} id
   * @property {string} url
   */

  /**
   * Re-renders the variant picker.
   * @param {Document | Element} newHtml - The new HTML.
   * @returns {NewProduct | undefined} Information about the new product if it has changed, otherwise undefined.
   */
  updateVariantPicker(newHtml) {
    /** @type {NewProduct | undefined} */
    let newProduct;

    const newVariantPickerSource = newHtml.querySelector(this.tagName.toLowerCase());

    if (!newVariantPickerSource) {
      throw new Error('No new variant picker source found');
    }

    // For combined listings, the product might have changed, so update the related data attribute.
    if (newVariantPickerSource instanceof HTMLElement) {
      const newProductId = newVariantPickerSource.dataset.productId;
      const newProductUrl = newVariantPickerSource.dataset.productUrl;

      if (newProductId && newProductUrl && this.dataset.productId !== newProductId) {
        newProduct = { id: newProductId, url: newProductUrl };
      }

      this.dataset.productId = newProductId;
      this.dataset.productUrl = newProductUrl;
    }

    morph(this, newVariantPickerSource, {
      ...MORPH_OPTIONS,
      getNodeKey: (node) => {
        if (!(node instanceof HTMLElement)) return undefined;
        const key = node.dataset.key;
        return key;
      },
    });
    this.updateVariantPickerCss();

    return newProduct;
  }

  updateVariantPickerCss() {
    const fieldsets = /** @type {HTMLFieldSetElement[]} */ (this.refs.fieldsets || []);

    // Batch all reads first across all fieldsets to avoid layout thrashing
    const measurements = fieldsets.map((_, index) => this.#getFieldsetMeasurements(index)).filter((m) => m !== null);

    // Batch all writes after all reads
    for (const measurement of measurements) {
      this.#applyFieldsetMeasurements(measurement);
    }
  }

  /**
   * Re-renders the desired element.
   * @param {Document} newHtml - The new HTML.
   * @param {string} elementSelector - The selector of the element to re-render.
   */
  updateElement(newHtml, elementSelector) {
    const element = this.closest(elementSelector);
    const newElement = newHtml.querySelector(elementSelector);

    if (!element || !newElement) {
      throw new Error(`No new element source found for ${elementSelector}`);
    }

    morph(element, newElement);
  }

  /**
   * Re-renders the entire main content.
   * @param {Document} newHtml - The new HTML.
   */
  updateMain(newHtml) {
    const main = document.querySelector('main');
    const newMain = newHtml.querySelector('main');

    if (!main || !newMain) {
      throw new Error('No new main source found');
    }

    morph(main, newMain);
  }

  /**
   * Gets the selected option.
   * @returns {HTMLInputElement | HTMLOptionElement | undefined} The selected option.
   */
  get selectedOption() {
    const selectedOption = this.querySelector('select option[selected], fieldset input:checked');

    if (!(selectedOption instanceof HTMLInputElement || selectedOption instanceof HTMLOptionElement)) {
      return undefined;
    }

    return selectedOption;
  }

  /**
   * Gets the selected option ID.
   * @returns {string | undefined} The selected option ID.
   */
  get selectedOptionId() {
    const { selectedOption } = this;
    if (!selectedOption) return undefined;
    const { optionValueId } = selectedOption.dataset;

    if (!optionValueId) {
      throw new Error('No option value ID found');
    }

    return optionValueId;
  }

  /**
   * Gets the selected options values.
   * @returns {string[]} The selected options values.
   */
  get selectedOptionsValues() {
    /** @type HTMLElement[] */
    const selectedOptions = Array.from(this.querySelectorAll('select option[selected], fieldset input:checked'));

    if (this.dataset.schoolButtonStandard === 'true' || this.dataset.productId === '9088901218585') {
      selectedOptions.sort((a, b) => Number(a.dataset.inputId?.split('-')[0]) - Number(b.dataset.inputId?.split('-')[0]));
    }

    return selectedOptions.map((option) => {
      const { optionValueId } = option.dataset;

      if (!optionValueId) throw new Error('No option value ID found');

      return optionValueId;
    });
  }
}

if (!customElements.get('variant-picker')) {
  customElements.define('variant-picker', VariantPicker);
}



















